import type * as React from "react";
import { type ReactNode, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { type RootState } from "../../../../redux/store";
import { applyTechValueGroup, clearTechFilters, updateTechValueAction } from "../../../../Search/redux/actions";
import { getIsApplyEnabled, getIsClearEnabledForFilters } from "../../../../Search/redux/searchSelectors";
import { renderItems } from "./DesktopTechFilterCategory";
import { useTranslation } from "react-i18next";
import { Button } from "~/common/components/Button/Button";
import type { TechFilter } from "~/Search/filterConfigModels";

interface Props {
  image?: ReactNode;
  items: TechFilter[];
}

interface CallbacksMap {
  [title: string]: (type: "min" | "max", value: number | undefined) => void;
}

export const MobileTechFilterCategory = (props: Props) => {
  const values = useSelector(selectRawValues);
  const { items, image } = props;
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const callbacks = useMemo(() => {
    const callbacks: CallbacksMap = {};
    items.forEach((item) => {
      callbacks[item.uid] = (type, val) => dispatch(updateTechValueAction(item.uid, type, val));
    });

    return callbacks;
  }, [items, dispatch]);

  const techFilterCodes = items.map((item) => item.uid);

  const isApplyEnabled = useSelector(getIsApplyEnabled);
  const isClearEnabled = useSelector((state: RootState) => getIsClearEnabledForFilters(state, techFilterCodes));

  const onClearClick = () => dispatch(clearTechFilters(techFilterCodes));

  const applyCb = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    dispatch(applyTechValueGroup());
  }, []);

  const renderChildren = renderItems(callbacks, values);

  return (
    <form onSubmit={applyCb}>
      <div className="scha__sa__tech-filter-category--mobile">
        {image}
        {renderChildren(items)}
        <div className="scha__sa__tech-filter-category__actions--mobile">
          <Button disabled={!isClearEnabled} onClick={onClearClick} variant={"text"}>
            {t("searchPage.refinement.button.clear")}
          </Button>
          <Button disabled={!isApplyEnabled} type={"submit"}>
            {t("searchPage.refinement.button.apply")}
          </Button>
        </div>
      </div>
    </form>
  );
};

const selectRawValues = (state: RootState) => state.srp.rawTechFilterValues;
